import { useState, useEffect, useRef, useCallback } from 'react';

// Only surface a red "Connection error" after this many CONSECUTIVE failed health
// polls. A single slow/dropped poll on an otherwise stable link must not flip the
// indicator — that flicker was the "Connection Error" / socket-instability bug.
const FAILURES_BEFORE_ERROR = 3;
// While under the failure threshold, retry quietly on this short cadence instead
// of waiting the full poll interval (silent reconnection).
const RETRY_DELAY_MS = 4000;
const POLL_INTERVAL_MS = 30000;
// The health probe's own timeout. A Cloud Run COLD START answers the first
// request in ~8s (measured 2026-10-06), which is exactly what the old 8s
// bound aborted — so the very first probe of a fresh session failed on a
// perfectly healthy backend.
export const HEALTH_TIMEOUT_MS = 20000;

// Before the first probe answers we do not know the backend's state; the hook
// used to start at 'disconnected', which the header rendered as a statement of
// fact for the whole first probe (and through any sub-threshold retry).
export const INITIAL_STATUS = 'checking';

export const useApiStatus = (apiService) => {
    const [apiStatus, setApiStatus] = useState(INITIAL_STATUS);
    const [provider, setProvider] = useState('unknown');
    const [modelName, setModelName] = useState('unknown');
    const [groundingEnabled, setGroundingEnabled] = useState(false);

    const failuresRef = useRef(0);
    const mountedRef = useRef(true);
    const retryTimerRef = useRef(null);

    const checkApiStatus = useCallback(async () => {
        try {
            const data = await apiService.checkHealth(HEALTH_TIMEOUT_MS);
            if (!mountedRef.current) return;
            failuresRef.current = 0;
            setApiStatus(data.model_loaded ? 'connected' : 'loading');
            setProvider(data.provider || 'unknown');
            setModelName(data.model || 'unknown');
            setGroundingEnabled(data.grounding_enabled || false);
        } catch (error) {
            if (!mountedRef.current) return;
            failuresRef.current += 1;
            if (failuresRef.current >= FAILURES_BEFORE_ERROR) {
                // Sustained failure — now it's a real connection problem.
                setApiStatus('error');
                setProvider('unknown');
                setModelName('unknown');
                setGroundingEnabled(false);
                // Keep probing on the short cadence so a recovered backend
                // turns the indicator green within seconds, not 30s later.
                if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
                retryTimerRef.current = setTimeout(() => { checkApiStatus(); }, RETRY_DELAY_MS * 2);
            } else {
                // Transient blip: keep the last-known-good status and retry soon
                // so a momentary flicker never shows the user a red error.
                if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
                retryTimerRef.current = setTimeout(() => { checkApiStatus(); }, RETRY_DELAY_MS);
            }
        }
    }, [apiService]);

    useEffect(() => {
        mountedRef.current = true;
        checkApiStatus();
        const interval = setInterval(checkApiStatus, POLL_INTERVAL_MS);
        return () => {
            mountedRef.current = false;
            clearInterval(interval);
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        };
    }, [checkApiStatus]);

    return { apiStatus, provider, modelName, groundingEnabled, checkApiStatus };
};